import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Handshake,
  History,
  Ban,
  RefreshCw,
  Send,
  UserCog,
  Wallet,
  Zap,
} from "lucide-react";
import { Button, FormModal, SearchBar } from "@veorest/ui";
import {
  adminCancelOrder,
  adminForceCompleteOrder,
  adminReassignOrderDriver,
  adminReblastOrder,
  fetchAdminDrivers,
  fetchAdminOrderDetail,
} from "@veorest/api-client";
import { ROUTES } from "../routes";
import { PageHeader } from "../components/shell/PageHeader";
import { EmptyState } from "../components/data/EmptyState";
import { OrderWalletSettlementCard } from "../components/wallets/OrderWalletSettlementCard";
import { formatDate, formatIqd, statusPillClass } from "../lib/format";

type ActionKind = "reassign" | "reblast" | "cancel" | "force" | null;

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}
const num = (v: unknown) => Number(v ?? 0);
const str = (v: unknown, fallback = "—") =>
  v === null || v === undefined || v === "" ? fallback : String(v);

const TERMINAL = new Set(["delivered", "completed", "cancelled", "rejected"]);

function driverLabel(row: Record<string, unknown>): string {
  return String(row.display_name || row.driver_name || row.name || "Driver");
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </span>
      <span className="text-sm font-semibold text-[var(--text-primary)] text-right break-words">
        {value}
      </span>
    </div>
  );
}

function Card({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface)] p-4">
      <div className="mb-2 flex items-center gap-2 text-[var(--text-muted)]">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-[var(--border-subtle)]">
          {icon}
        </span>
        <h3 className="text-sm font-black uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </section>
  );
}

export default function OrderDetailPage({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [action, setAction] = useState<ActionKind>(null);
  const [reason, setReason] = useState("");
  const [driverId, setDriverId] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [blastTarget, setBlastTarget] = useState<
    "auto" | "partner" | "global" | "business"
  >("auto");
  const [forceConfirmed, setForceConfirmed] = useState(false);

  const detail = useQuery({
    queryKey: ["admin-order", id],
    queryFn: () => fetchAdminOrderDetail(id),
  });

  const drivers = useQuery({
    queryKey: ["admin-order-drivers", driverSearch],
    queryFn: () =>
      fetchAdminDrivers({ limit: 100, search: driverSearch || undefined }),
    enabled: action === "reassign",
  });

  const close = () => {
    setAction(null);
    setReason("");
    setDriverId("");
    setDriverSearch("");
    setBlastTarget("auto");
    setForceConfirmed(false);
  };

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin-order", id] });
    void qc.invalidateQueries({ queryKey: ["admin-orders"] });
  };

  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Action failed");

  const reassign = useMutation({
    mutationFn: () =>
      adminReassignOrderDriver(id, { driverId, reason: reason || undefined }),
    onSuccess: () => {
      toast.success("Driver reassigned");
      close();
      invalidate();
    },
    onError,
  });
  const reblast = useMutation({
    mutationFn: () =>
      adminReblastOrder(id, { target: blastTarget, reason: reason || undefined }),
    onSuccess: () => {
      toast.success("Order re-blasted");
      close();
      invalidate();
    },
    onError,
  });
  const cancel = useMutation({
    mutationFn: () => adminCancelOrder(id, { reason: reason || undefined }),
    onSuccess: () => {
      toast.success("Order cancelled");
      close();
      invalidate();
    },
    onError,
  });
  const force = useMutation({
    mutationFn: () =>
      adminForceCompleteOrder(id, { reason: reason || undefined }),
    onSuccess: () => {
      toast.success("Order force-completed");
      close();
      invalidate();
    },
    onError,
  });

  const data = detail.data;
  const order = asRecord(data?.order);
  const partner = asRecord(data?.partner);
  const driver = asRecord(data?.driver);
  const settlement = asRecord(data?.settlement);
  const ledger = asRecord(data?.ledger);

  const timeline = useMemo(() => {
    const rows: Array<{ source: string; label: string; at: string }> = [];
    for (const e of data?.events ?? []) {
      rows.push({
        source: "partner",
        label: str(e.event, "event"),
        at: str(e.created_at, ""),
      });
    }
    for (const e of data?.dispatch_events ?? []) {
      rows.push({
        source: "dispatch",
        label: str(e.event, "event"),
        at: str(e.created_at, ""),
      });
    }
    return rows
      .filter((r) => r.at)
      .sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [data]);

  const status = str(order?.status, "");
  const isTerminal = TERMINAL.has(status.toLowerCase());
  const busy =
    reassign.isPending || reblast.isPending || cancel.isPending || force.isPending;

  if (detail.isLoading) {
    return (
      <div className="menu-page">
        <PageHeader eyebrow="Order desk" title="Order" subtitle="Loading…" />
        <div className="h-40 rounded-xl skeleton" />
      </div>
    );
  }

  if (detail.isError || !order) {
    return (
      <div className="menu-page">
        <PageHeader eyebrow="Order desk" title="Order" subtitle="Not available" />
        <EmptyState
          icon={<Ban className="h-5 w-5" />}
          title="Order not found"
          body={
            detail.isError
              ? detail.error instanceof Error
                ? detail.error.message
                : "Failed to load order."
              : "The order no longer exists."
          }
        />
        <div className="mt-3">
          <Button variant="secondary" onClick={() => navigate(ROUTES.ORDERS)}>
            Back to orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Order desk"
        title={`Order ${str(order.display_number || id.slice(0, 8))}`}
        subtitle={`${str(order.restaurant_name, "")} · ${str(order.customer_name, "Customer")}`}
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={() => navigate(ROUTES.ORDERS)}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <span className={statusPillClass(status)}>{status || "unknown"}</span>
        {partner?.id ? (
          <span className="rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-1 text-xs font-bold text-[var(--text-secondary)]">
            partner stage: {str(order.stage, "—")}
          </span>
        ) : null}
        {order.partner_declined_at ? (
          <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-600">
            declined {str(order.partner_decline_reason, "")}
          </span>
        ) : null}
        {order.return_to_store === true ? (
          <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
            return to store
          </span>
        ) : null}
      </div>

      {!isTerminal ? (
        <div className="mb-4 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setAction("reassign")} disabled={busy}>
            <UserCog className="mr-1 h-4 w-4" /> Reassign driver
          </Button>
          <Button variant="secondary" onClick={() => setAction("reblast")} disabled={busy}>
            <Send className="mr-1 h-4 w-4" /> Re-blast
          </Button>
          <Button variant="secondary" onClick={() => setAction("cancel")} disabled={busy}>
            <Ban className="mr-1 h-4 w-4" /> Cancel order
          </Button>
          <Button variant="secondary" onClick={() => setAction("force")} disabled={busy}>
            <Zap className="mr-1 h-4 w-4" /> Force complete
          </Button>
        </div>
      ) : (
        <p className="mb-4 text-xs font-semibold text-[var(--text-muted)]">
          Terminal state — no order actions available.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Order" icon={<History className="h-4 w-4" />}>
          <InfoRow label="Client" value={str(order.restaurant_name)} />
          <InfoRow label="Customer" value={str(order.customer_name)} />
          <InfoRow label="Phone" value={str(order.customer_phone)} />
          <InfoRow label="Address" value={str(order.customer_address)} />
          <InfoRow label="Fulfillment" value={str(order.fulfillment_type || order.order_type)} />
          <InfoRow label="Payment" value={`${str(order.payment_method)} · ${str(order.payment_status)}`} />
          <InfoRow label="Total" value={formatIqd(num(order.total))} />
          <InfoRow label="Delivery fee" value={`${formatIqd(num(order.delivery_fee))} · ${str(order.delivery_fee_responsibility)}`} />
          <InfoRow label="Created" value={formatDate(str(order.created_at, ""))} />
          <InfoRow label="Accepted" value={order.accepted_at ? formatDate(String(order.accepted_at)) : "—"} />
          <InfoRow label="Ready" value={order.ready_at ? formatDate(String(order.ready_at)) : "—"} />
          <InfoRow label="Completed" value={order.completed_at ? formatDate(String(order.completed_at)) : "—"} />
          {status.toLowerCase() === "rejected" ? (
            <>
              <InfoRow
                label="Rejected at"
                value={order.rejected_at ? formatDate(String(order.rejected_at)) : "—"}
              />
              <InfoRow
                label="Return to store"
                value={order.return_to_store === true ? "yes" : "no"}
              />
              <InfoRow
                label="Fee charged to client"
                value={
                  order.rejection_charge_delivery === true
                    ? "yes"
                    : order.rejection_charge_delivery === false
                      ? "no"
                      : "—"
                }
              />
              <InfoRow
                label="Driver keeps fee"
                value={
                  order.rejection_keep_driver_fee === true
                    ? "yes"
                    : order.rejection_keep_driver_fee === false
                      ? "no"
                      : "—"
                }
              />
            </>
          ) : null}
        </Card>

        <Card title="Partner" icon={<Handshake className="h-4 w-4" />}>
          {partner ? (
            <>
              <InfoRow label="Name" value={str(partner.name)} />
              <InfoRow label="Handler" value={partner.is_handler ? "yes" : "no (declined)"} />
              <InfoRow
                label="Accepted"
                value={partner.accepted_at ? formatDate(String(partner.accepted_at)) : "awaiting"}
              />
              <InfoRow
                label="Declined"
                value={partner.declined_at ? formatDate(String(partner.declined_at)) : "—"}
              />
              {partner.decline_reason ? (
                <InfoRow label="Reason" value={String(partner.decline_reason)} />
              ) : null}
            </>
          ) : (
            <p className="text-sm text-[var(--text-muted)]">
              Not partner-handled (OELOX-direct or unlinked).
            </p>
          )}
          <div className="mt-2 border-t border-[var(--border-subtle)] pt-2">
            <InfoRow label="Driver" value={driver ? driverLabel(driver) : "none"} />
            {driver ? (
              <>
                <InfoRow label="Driver fleet" value={str(driver.fleet_type)} />
                <InfoRow label="Driver phone" value={str(driver.phone)} />
                <InfoRow label="Driver status" value={str(driver.driver_status)} />
              </>
            ) : null}
          </div>
        </Card>

        <Card title="Money" icon={<Wallet className="h-4 w-4" />}>
          <InfoRow label="Partner fee" value={`${formatIqd(num(order.partner_fee_amount))} · ${str(order.partner_fee_type)} · ${str(order.partner_settlement_mode)}`} />
          <InfoRow
            label="Ledger"
            value={
              ledger
                ? `${str(ledger.settlement_status)} · ${formatIqd(num(ledger.fee_amount))}${ledger.void_reason ? ` · void: ${ledger.void_reason}` : ""}`
                : "none"
            }
          />
          <InfoRow
            label="Settlement"
            value={
              settlement
                ? `${str(settlement.payer)} · oelox ${formatIqd(num(settlement.oelox_fee))} · client ${formatIqd(num(settlement.client_charge))} · driver ${formatIqd(num(settlement.driver_credit))}`
                : "none (trip not settled)"
            }
          />
          <p className="mt-2 border-t border-[var(--border-subtle)] pt-2 text-xs font-semibold text-[var(--text-muted)]">
            Partner fee / ledger rows above are the frozen legacy accrual rail — live wallet
            settlement is in the card below.
          </p>
        </Card>

        <OrderWalletSettlementCard orderId={id} />

        <Card title="Timeline" icon={<RefreshCw className="h-4 w-4" />}>
          {timeline.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No events recorded yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {timeline.map((row, idx) => (
                <li
                  key={`${row.source}-${row.at}-${idx}`}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="font-semibold text-[var(--text-primary)]">
                    {row.label}
                  </span>
                  <span className="text-xs text-[var(--text-muted)]">
                    {row.source} · {formatDate(row.at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* ── Action modals ────────────────────────────────────────────────── */}
      <FormModal
        open={action === "reassign"}
        onClose={close}
        title="Reassign driver"
        subtitle="Assign an active driver to this order. Partner-handled orders accept the partner fleet or OELOX global drivers."
        icon={<UserCog className="h-5 w-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={() => reassign.mutate()}
              disabled={!driverId || reassign.isPending}
            >
              {reassign.isPending ? "Assigning…" : "Assign driver"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <SearchBar
            value={driverSearch}
            onChange={(e) => setDriverSearch(e.target.value)}
            placeholder="Search drivers…"
            className="admin-search"
          />
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-xl border border-[var(--border-subtle)] p-2">
            {(drivers.data?.items ?? []).map((row) => {
              const d = row as Record<string, unknown>;
              const did = String(d.id ?? "");
              const active = did === driverId;
              return (
                <button
                  key={did}
                  type="button"
                  onClick={() => setDriverId(did)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm ${
                    active
                      ? "bg-[var(--surface-muted)] font-bold"
                      : "hover:bg-[var(--surface-muted)]"
                  }`}
                >
                  <span>{driverLabel(d)}</span>
                  <span className="text-xs text-[var(--text-muted)]">
                    {String(d.fleet_type || "restaurant")}
                  </span>
                </button>
              );
            })}
            {drivers.isLoading ? (
              <p className="p-3 text-xs text-[var(--text-muted)]">Loading…</p>
            ) : null}
          </div>
          <input
            className="w-full rounded-xl border border-[var(--border-subtle)] bg-transparent px-3 py-2 text-sm"
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </FormModal>

      <FormModal
        open={action === "reblast"}
        onClose={close}
        title="Re-blast order"
        subtitle="Broadcast this order to drivers again. Partner-handled orders notify the partner fleet."
        icon={<Send className="h-5 w-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={() => reblast.mutate()} disabled={reblast.isPending}>
              {reblast.isPending ? "Blasting…" : "Re-blast"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <label className="block text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
            Target
          </label>
          <select
            className="w-full rounded-xl border border-[var(--border-subtle)] bg-transparent px-3 py-2 text-sm"
            value={blastTarget}
            onChange={(e) =>
              setBlastTarget(e.target.value as typeof blastTarget)
            }
          >
            <option value="auto">Auto (partner fleet / previous source)</option>
            <option value="partner">Partner fleet</option>
            <option value="global">OELOX global drivers</option>
            <option value="business">Client business drivers</option>
          </select>
          <input
            className="w-full rounded-xl border border-[var(--border-subtle)] bg-transparent px-3 py-2 text-sm"
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </FormModal>

      <FormModal
        open={action === "cancel"}
        onClose={close}
        title="Cancel order"
        subtitle="Cancels the order, releases the driver, voids the partner fee and notifies the store and partner."
        icon={<Ban className="h-5 w-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close}>
              Keep order
            </Button>
            <Button onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              {cancel.isPending ? "Cancelling…" : "Cancel order"}
            </Button>
          </div>
        }
      >
        <input
          className="w-full rounded-xl border border-[var(--border-subtle)] bg-transparent px-3 py-2 text-sm"
          placeholder="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </FormModal>

      <FormModal
        open={action === "force"}
        onClose={close}
        title="Force complete"
        subtitle="Closes the trip as delivered and settles wallets. Only for trips that physically happened but were not closed in the Driver app."
        icon={<Zap className="h-5 w-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={() => force.mutate()}
              disabled={!forceConfirmed || force.isPending}
            >
              {force.isPending ? "Completing…" : "Force complete"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={forceConfirmed}
              onChange={(e) => setForceConfirmed(e.target.checked)}
            />
            <span>
              I confirm this trip physically happened and should be settled.
            </span>
          </label>
          <input
            className="w-full rounded-xl border border-[var(--border-subtle)] bg-transparent px-3 py-2 text-sm"
            placeholder="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </FormModal>
    </div>
  );
}
